import { useState, useEffect, useContext, createContext, useRef, useCallback } from 'react';
import apiClient from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import socketService from '../services/socketService';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const fetchedRef = useRef(false);
  const userId = user?.id;

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get('/api/subscription/current');

      if (response.success) {
        setSubscription(response.data.subscription);
      } else {
        setSubscription(null);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userId]);

  // Fetch once when user is authenticated (keyed on userId to avoid duplicates)
  useEffect(() => {
    if (isAuthenticated && userId) {
      if (!fetchedRef.current) {
        fetchedRef.current = true;
        fetchSubscription();
      }
    } else {
      fetchedRef.current = false;
      setSubscription(null);
      setLoading(false);
    }
  }, [isAuthenticated, userId, fetchSubscription]);

  // Setup real-time subscription updates via socket
  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    let cleanupFns = [];

    const handleSubscriptionUpdate = (data) => {
      if (data.type === 'subscription_created' || data.type === 'subscription_updated') {
        setSubscription(prevSubscription => ({
          ...prevSubscription,
          ...data.subscription,
          currentPeriodStart: data.subscription.currentPeriodStart ?
            new Date(data.subscription.currentPeriodStart).toISOString() : null,
          currentPeriodEnd: data.subscription.currentPeriodEnd ?
            new Date(data.subscription.currentPeriodEnd).toISOString() : null
        }));

        setTimeout(() => fetchSubscription(), 1000);
      }
    };

    const handlePaymentUpdate = (data) => {
      if (data.type === 'payment_completed') {
        setTimeout(() => fetchSubscription(), 1000);
      }
    };

    socketService.connect().then(() => {
      const unsub1 = socketService.on('subscription_updated', handleSubscriptionUpdate);
      const unsub2 = socketService.on('payment_updated', handlePaymentUpdate);
      cleanupFns = [unsub1, unsub2];
    }).catch(() => {});

    return () => {
      cleanupFns.forEach(fn => fn && fn());
    };
  }, [isAuthenticated, userId, fetchSubscription]);

  // Periodic refresh only for past_due or trialing
  useEffect(() => {
    if (!isAuthenticated || !subscription) return;

    let intervalId;

    if (subscription?.status === 'past_due') {
      intervalId = setInterval(fetchSubscription, 60000);
    } else if (subscription?.status === 'trialing') {
      intervalId = setInterval(fetchSubscription, 300000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAuthenticated, subscription?.status, fetchSubscription]);

  // Helper functions to check subscription status
  const hasActiveSubscription = () => {
    if (!subscription) return false;
    
    const activeStatuses = ['active', 'trialing', 'past_due', 'canceled_at_period_end'];
    return activeStatuses.includes(subscription.status);
  };

  const isTrialing = () => {
    return subscription?.status === 'trialing';
  };

  const isPastDue = () => {
    return subscription?.status === 'past_due';
  };

  const getSubscriptionStatus = () => {
    if (!subscription) return 'none';
    return subscription.status;
  };

  const getPlanName = () => {
    return subscription?.plan || 'none';
  };

  const getDaysUntilExpiry = () => {
    if (!subscription || !subscription.currentPeriodEnd) {
      return null;
    }

    const today = new Date();
    const expiryDate = new Date(subscription.currentPeriodEnd);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const canAccessFeature = (feature) => {
    if (!hasActiveSubscription()) return false;

    // Feature-specific logic based on plan
    const plan = getPlanName().toLowerCase();

    switch (feature) {
      case 'integration':
      case 'sources':
      case 'appearance':
      case 'advanced':
        return hasActiveSubscription();
      case 'analytics':
      case 'transcript_export':
        return hasActiveSubscription() && (plan === 'pro' || plan === 'basic');
      case 'custom_branding':
      case 'advanced_analytics':
      case 'priority_support':
        return hasActiveSubscription() && plan === 'pro';
      default:
        return hasActiveSubscription();
    }
  };

  // Manual sync with Stripe (backup function)
  const syncWithStripe = async () => {
    if (!isAuthenticated || !user) return false;

    try {
      setLoading(true);
      const response = await apiClient.post('/api/subscription/sync');
      
      if (response.success) {
        setSubscription(response.data.subscription);
        setError(null);
        return response.data.syncPerformed;
      } else {
        setError(response.message || 'Sync failed');
        return false;
      }
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    subscription,
    loading,
    error,
    hasActiveSubscription,
    isTrialing,
    isPastDue,
    getSubscriptionStatus,
    getPlanName,
    getDaysUntilExpiry,
    canAccessFeature,
    refetchSubscription: fetchSubscription,
    syncWithStripe
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};